import { useEffect, useState } from 'react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { AdManagementService, type MetaAdAccount, type MetaConnectionRead, type MetaPage } from '../../client'

const TRIGGER =
  'group flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none'
const PANEL =
  'z-[60] w-(--button-width) divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg [--anchor-gap:4px] [--anchor-max-height:9rem]'
const OPTION =
  'flex w-full cursor-pointer items-center justify-between px-3 py-2.5 text-left text-sm transition hover:bg-gray-50 data-selected:bg-indigo-50'
const CHEVRON = 'ml-2 size-4 shrink-0 text-gray-400 transition-transform group-data-open:rotate-180'

type Props = {
  brandId: number
  initialAdAccountId?: string | null
  initialPageId?: string | null
  onSaved: (connection: MetaConnectionRead) => void
  onBack: () => void
}

export default function AccountPageSelector({ brandId, initialAdAccountId, initialPageId, onSaved, onBack }: Props) {
  const [adAccounts, setAdAccounts] = useState<MetaAdAccount[]>([])
  const [pages, setPages] = useState<MetaPage[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [adAccountId, setAdAccountId] = useState(initialAdAccountId ?? '')
  const [pageId, setPageId] = useState(initialPageId ?? '')
  const [error, setError] = useState('')

  const adAccount = adAccounts.find(({ id }) => id === adAccountId)
  const page = pages.find(({ id }) => id === pageId)

  useEffect(() => {
    Promise.all([AdManagementService.getAdAccounts(brandId), AdManagementService.getPages(brandId)])
      .then(([accounts, fetchedPages]) => {
        setAdAccounts(accounts)
        setPages(fetchedPages)
      })
      .catch(() => setError('Failed to load Meta accounts. Please try again.'))
      .finally(() => setLoading(false))
  }, [brandId])

  async function save() {
    setSaving(true)
    setError('')

    try {
      onSaved(
        await AdManagementService.selectDefaults({
          brand_id: brandId,
          ad_account_id: adAccountId,
          ad_account_name: adAccount?.name ?? '',
          page_id: pageId,
          page_name: page?.name ?? '',
        }),
      )
    } catch {
      setError('Failed to save defaults. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">Select which ad account and Facebook page to use for running ads.</p>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Ad Account</label>
        {loading ? (
          <div className="h-10 animate-pulse rounded-lg bg-gray-200" />
        ) : (
          <Listbox value={adAccountId} onChange={setAdAccountId}>
            <ListboxButton className={TRIGGER}>
              {adAccount ? (
                <span className="truncate text-gray-800">
                  {adAccount.name} <span className="text-xs text-gray-400">({adAccount.id})</span>
                </span>
              ) : (
                <span className="text-gray-400">Select an ad account</span>
              )}
              <ChevronDownIcon className={CHEVRON} />
            </ListboxButton>
            <ListboxOptions anchor="bottom start" className={PANEL}>
              {adAccounts.map((account) => (
                <ListboxOption key={account.id} value={account.id} className={OPTION}>
                  <span className="truncate text-gray-800">
                    {account.name} <span className="text-xs text-gray-400">({account.id})</span>
                  </span>
                  <span
                    className={`ml-3 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                      account.account_status === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {account.account_status === 1 ? 'Active' : 'Disabled'}
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Facebook Page</label>
        {loading ? (
          <div className="h-10 animate-pulse rounded-lg bg-gray-200" />
        ) : (
          <Listbox value={pageId} onChange={setPageId}>
            <ListboxButton className={TRIGGER}>
              {page ? (
                <span className="truncate text-gray-800">
                  {page.name} — {page.category}
                </span>
              ) : (
                <span className="text-gray-400">Select a page</span>
              )}
              <ChevronDownIcon className={CHEVRON} />
            </ListboxButton>
            <ListboxOptions anchor="bottom start" className={PANEL}>
              {pages.map((option) => (
                <ListboxOption key={option.id} value={option.id} className={OPTION}>
                  <span className="truncate text-gray-800">
                    {option.name} — {option.category}
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        )}
      </div>

      {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="button"
          disabled={!adAccountId || !pageId || saving}
          onClick={() => void save()}
          className="rounded bg-purple-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-purple-950 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save & Continue'}
        </button>
      </div>
    </div>
  )
}
